import classNames from 'classnames'

export const BUTTON_VARIANTS = [
  'black',
  'gray',
  'white',
  'oxide',
  'oxide-bright',
  'oxide-core',
  'oxide-300',
  'oxide-primary',
  'copper',
  'copper-600',
  'lightgray',
  'lightcoppergray',
  'offblack',
  'offwhite',
  'lightened-50',
  'core-gray-100',
  'core-gray-300',
  'core-gray-400',
  'core-gray-600',
  'core-gray-700',
  'core-gray-900',
  'plain',
  'hos-orange',
  'core-gray-800',
  'core-gray-900',
  'gatg-pink',
  'whatsapp',
] as const

export type ButtonVariant = (typeof BUTTON_VARIANTS)[number]

type VariantClasses = {
  [key: string]: string
}

const normalState: VariantClasses = {
  black: 'bg-core-gray-950 border-core-gray-950 text-white',
  'black-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'black-outline': 'border-core-gray-600 bg-transparent text-core-gray-950',
  'black-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  gray: 'bg-core-gray-300 border-core-gray-300 text-core-gray-950',
  'gray-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'gray-outline': 'border-core-gray-300 bg-transparent text-core-gray-950',
  'gray-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  white: 'bg-white border-white text-core-gray-950',
  'white-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'white-outline': 'border-white text-white hover:bg-shaded-50 active:bg-shaded-200',
  'white-outline-disabled': 'bg-core-gray-200 text-core-gray-400',
  'white-danger': 'bg-white text-warm-01',

  oxide: 'border-oxide bg-oxide text-white',
  'oxide-disabled': 'border-oxide-extra-light bg-oxide-extra-light text-core-gray-400',
  'oxide-outline': 'border-oxide bg-transparent text-oxide',
  'oxide-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'oxide-bright': 'border-oxide-bright bg-oxide-bright text-core-gray-950',
  'oxide-bright-disabled': 'border-oxide-bright/80 bg-oxide-bright/80 text-core-gray-400',
  'oxide-bright-outline': 'border-oxide-bright bg-transparent text-oxide-bright',
  'oxide-bright-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'oxide-core': 'border-core-oxide bg-core-oxide text-core-gray-950',
  'oxide-core-disabled': 'border-core-oxide/80 bg-core-oxide/80 text-core-gray-400',
  'oxide-core-outline': 'border-core-oxide bg-transparent text-core-oxide',
  'oxide-core-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'oxide-300': 'border-oxide-300 bg-oxide-300 text-core-gray-950',
  'oxide-300-disabled': 'border-oxide-300/20  bg-oxide-300/20 text-core-gray-400',
  'oxide-300-outline': 'border-oxide-300 bg-transparent text-core-oxide',
  'oxide-300-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'oxide-primary': 'border-oxide-primary bg-oxide-primary text-white',
  'oxide-primary-disabled': 'border-oxide-primary/80 bg-oxide-primary/80 text-core-gray-400',
  'oxide-primary-outline': 'border-oxide-primary bg-transparent text-oxide-primary',
  'oxide-primary-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  copper: 'border-copper bg-copper text-white',
  'copper-disabled': 'border-copper-extra-light bg-copper-extra-light text-core-gray-400',
  'copper-outline': 'border-copper bg-transparent text-copper',
  'copper-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'copper-600': 'border-copper-600 bg-copper-600 text-core-gray-100',
  'copper-600-disabled': 'border-copper-200 bg-copper-200 text-core-gray-400',
  'copper-600-outline': 'border-copper-600 bg-transparent text-copper-600',
  'copper-600-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  lightgray: 'bg-core-gray-200 border-core-gray-200 text-core-gray-950',
  'lightgray-disabled': 'bg-core-gray-200 border-core-gray-200 text-core-gray-500',
  'lightgray-outline': 'border-core-gray-200 bg-transparent text-core-gray-950',
  'lightgray-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  lightcoppergray: 'bg-core-gray-900 text-copper-lighter border-0',
  'lightcoppergray-disabled': 'border-copper-lighter bg-core-gray-500 text-core-gray-800',
  'lightcoppergray-outline': 'border-[1px] text-copper-lighter',
  'lightcoppergray-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  offblack: 'bg-[#3E3E3E] border-[#3E3E3E] text-white',
  'offblack-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'offblack-outline': 'border-[#3E3E3E] bg-transparent text-[#3E3E3E]',
  'offblack-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  offwhite: 'bg-white/20 border-transparent backdrop-blur-[48px] text-white',
  'offwhite-disabled': 'bg-white/20 text-white/40 border-transparent',
  'offwhite-outline': 'border-white',
  'offwhite-outline-disabled': 'bg-white/20 text-white/40 border-transparent',

  'lightened-50': 'bg-lightened-50 border-lightened-50 text-white',
  'lightened-50-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'lightened-50-outline': 'border-lightened-50 text-white',
  'lightened-50-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'hos-orange': 'border-hos-orange bg-hos-orange text-white',
  'hos-orange-disabled': 'border-hos-orange-extra-light bg-hos-orange-extra-light text-core-gray-400',
  'hos-orange-outline': 'border-hos-orange bg-transparent text-hos-orange',
  'hos-orange-outline-disabled': 'border-hos-orange-extra-light bg-transparent text-core-gray-400',

  'core-gray-100': 'bg-core-gray-100 border-core-gray-100 text-core-gray-800',
  'core-gray-100-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-100',
  'core-gray-100-outline': 'border-core-gray-100 bg-transparent text-core-gray-800',
  'core-gray-100-outline-disabled': 'bg-core-gray-200 text-black',
  'core-gray-100-danger': 'bg-core-gray-100 text-warm-01',

  'core-gray-300': 'bg-core-gray-300 border-core-gray-300 text-black',
  'core-gray-300-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-300',
  'core-gray-300-outline': 'border-core-gray-300 bg-transparent text-core-gray-950',
  'core-gray-300-outline-disabled': 'bg-core-gray-200 text-black',

  'core-gray-800': 'bg-core-gray-800 border-core-gray-800 text-white',
  'core-gray-800-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-800',
  'core-gray-800-outline': 'border-core-gray-800 bg-transparent text-core-gray-950',
  'core-gray-800-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'core-gray-900': 'bg-core-gray-900 border-core-gray-900 text-white',
  'core-gray-900-disabled': 'border-core-gray-500 bg-core-gray-500 text-core-gray-900',
  'core-gray-900-outline': 'border-core-gray-900 bg-transparent text-core-gray-950',
  'core-gray-900-outline-disabled': 'bg-core-gray-200 text-core-gray-400',

  'gatg-pink': 'bg-gatg-pink border-gatg-pink text-white',
  'gatg-pink-disabled': 'bg-gatg-pink border-gatg-pink text-white',
  'gatg-pink-outline': 'border-gatg-pink bg-transparent text-white',
  'gatg-pink-outline-disabled': 'bg-gatg-pink text-white',

  whatsapp: 'bg-[#30D170] border-[#30D170] text-white',
  'whatsapp-disabled': 'bg-[#C8F1D3] border-[#C8F1D3] text-white',
}

const hoverState: VariantClasses = {
  black: 'hover:bg-core-gray-900 hover:border-core-gray-900',
  'black-outline': 'hover:bg-core-gray-900 hover:text-white hover:border-core-gray-900',

  gray: 'hover:bg-core-gray-400 hover:border-core-gray-400',
  'gray-outline': 'hover:bg-core-gray-400 hover:text-core-gray-950 hover:border-core-gray-400',

  white: 'hover:bg-core-gray-200 hover:border-core-gray-200',
  'white-outline': 'hover:bg-white hover:text-core-gray-600 hover:border-gray-50',
  'white-danger': 'hover:bg-warm-01 hover:text-white',

  oxide: 'hover:bg-oxide-dark hover:border-oxide-dark',
  'oxide-outline': 'hover:bg-oxide-dark hover:text-white hover:border-oxide-dark',

  'oxide-bright': 'hover:bg-oxide-bright/50 hover:border-oxide-bright/50',
  'oxide-bright-outline': 'hover:bg-oxide-bright/50 hover:text-white hover:border-oxide-bright/50',

  'oxide-core': 'hover:bg-core-oxide/50 hover:border-core-oxide/50',
  'oxide-core-outline': 'hover:bg-core-oxide/50 hover:text-white hover:border-core-oxide/50',

  'oxide-300': 'hover:bg-oxide-300/50 hover:border-oxide-300/50',
  'oxide-300-outline': 'hover:bg-oxide-300/50 hover:text-white hover:border-oxide-300/50',

  copper: 'hover:bg-copper-dark hover:border-copper-dark',
  'copper-outline': 'hover:bg-copper-dark hover:text-white hover:border-copper-dark',

  'copper-600': 'hover:bg-copper-700 hover:border-copper-700',
  'copper-600-outline': 'hover:bg-copper-700 hover:text-core-gray-100 hover:border-copper-700',

  lightgray: 'hover:bg-core-gray-400 hover:border-core-gray-400',
  'lightgray-outline': 'hover:border-core-gray-400',

  lightcoppergray: 'hover:bg-core-gray-800',
  'lightcoppergray-outline': 'hover:text-copper-light hover:border-copper-light',

  offblack: 'hover:opacity-80',
  'offblack-outline': 'hover:opacity-80',

  offwhite: 'hover:bg-white/30',
  'offwhite-outline': 'hover:bg-white/30',

  'lightened-50': 'hover:bg-lightened-100 hover:border-lightened-100',
  'lightened-50-outline': 'hover:bg-lightened-100 hover:text-white hover:border-lightened-100',

  'hos-orange': 'hover:bg-hos-orange-dark hover:border-hos-orange-dark',
  'hos-orange-outline': 'hover:bg-hos-orange-dark hover:text-white hover:border-hos-orange-dark',

  'core-gray-300': 'hover:bg-core-gray-400 hover:border-core-gray-400',
  'core-gray-300-outline': 'hover:bg-core-gray-400 hover:text-white hover:border-core-gray-400',

  'core-gray-800': 'hover:bg-core-gray-900 hover:border-core-gray-900',
  'core-gray-800-outline': 'hover:bg-core-gray-900 hover:text-white hover:border-core-gray-900',

  'core-gray-900': 'hover:bg-core-gray-950 hover:border-core-gray-950',
  'core-gray-900-outline': 'hover:bg-core-gray-950 hover:text-white hover:border-core-gray-950',

  'gatg-pink': 'hover:bg-[#F5F5F4] hover:border-[#F5F5F4] hover:text-black',
  'gatg-pink-outline': 'hover:border-[#F5F5F4] hover:text-black',

  whatsapp: 'hover:bg-[#35E67B] hover:border-[#35E67B]',
  'whatsapp-disabled': 'hover:bg-[#C8F1D3] hover:border-[#C8F1D3] text-white',
}

const activeState: VariantClasses = {
  black: 'active:bg-core-gray-800 active:border-core-gray-800',
  'black-outline': 'active:bg-core-gray-800 active:border-core-gray-800 active:text-white',

  gray: 'active:bg-core-gray-500 active:border-core-gray-500',
  'gray-outline': 'active:bg-core-gray-500 active:border-core-gray-500 active:text-core-gray-950',

  white: 'active:bg-core-gray-300 active:border-core-gray-300',
  'white-outline': 'active:bg-core-gray-200 active:border-core-gray-200 active:text-core-gray-950',

  oxide: 'active:bg-oxide-extra-dark active:border-oxide-extra-dark',
  'oxide-outline': 'active:bg-oxide-extra-dark active:border-oxide-extra-dark active:text-white',

  'oxide-bright': 'active:bg-oxide-bright/35 active:border-oxide-bright/35',
  'oxide-bright-outline': 'active:bg-oxide-bright/35 active:border-oxide-bright/35 active:text-white',

  'oxide-core': 'active:bg-core-oxide/35 active:border-core-oxide/35',
  'oxide-core-outline': 'active:bg-core-oxide/35 active:border-core-oxide/35 active:text-white',

  'oxide-300': 'active:bg-oxide-300 active:border-core-oxide/35',
  'oxide-300-outline': 'active:bg-oxide-300 active:border-core-oxide/35 active:text-white',

  copper: 'active:bg-copper-extra-dark active:border-copper-extra-dark',
  'copper-outline': 'active:bg-copper-extra-dark active:border-copper-extra-dark active:text-white',

  'copper-600': 'active:bg-copper-800 active:border-copper-800',
  'copper-600-outline': 'active:bg-copper-800 active:border-copper-800 active:text-core-gray-100',

  lightgray: 'active:bg-core-gray-200 active:border-core-gray-200',
  'lightgray-outline': 'active:bg-core-gray-200 active:border-core-gray-200',

  lightcoppergray: 'active:bg-core-gray-700',
  'lightcoppergray-outline': 'active:text-copper active:border-copper',

  offblack: 'active:opacity-70',
  'offblack-outline': 'active:opacity-70',

  offwhite: 'active:opacity-70',
  'offwhite-outline': 'active:opacity-70',

  'lightened-50': 'active:bg-lightened-200 active:border-lightened-200',
  'lightened-50-outline': 'active:bg-lightened-200 active:border-lightened-200 active:text-white',

  'hos-orange': 'active:bg-hos-orange-extra-dark active:border-hos-orange-extra-dark',
  'hos-orange-outline': 'active:bg-hos-orange-extra-dark active:border-hos-orange-extra-dark active:text-white',

  'core-gray-300': 'active:bg-core-gray-500 active:border-core-gray-500',
  'core-gray-300-outline': 'active:bg-core-gray-500 active:border-core-gray-500 active:text-white',

  'core-gray-800': 'active:bg-core-gray-950 active:border-core-gray-950',
  'core-gray-800-outline': 'active:bg-core-gray-950 active:border-core-gray-950 active:text-white',

  'core-gray-900': 'active:bg-core-gray-950 active:border-core-gray-950',
  'core-gray-900-outline': 'active:bg-core-gray-950 active:border-core-gray-950 active:text-white',

  'gatg-pink': 'active:bg-core-gray-700 active:border-core-gray-700 active:text-white',
  'gatg-pink-outline': 'active:bg-core-gray-700 active:border-core-gray-700 active:text-white',

  whatsapp: 'active:bg-whatsApp active:bg-whatsApp',
  'whatsapp-disabled': 'hover:bg-[#C8F1D3] hover:border-[#C8F1D3] text-white',
}

interface VariantClassRequest {
  variant: ButtonVariant
  outline?: boolean
  disabled?: boolean
  danger?: boolean
}

export function getVariantClasses({ variant, outline, disabled, danger }: VariantClassRequest) {
  let finalVariant = variant

  if (outline) {
    finalVariant += '-outline'
  }

  if (disabled) {
    finalVariant += '-disabled'
  }

  if (danger) {
    finalVariant += '-danger'
  }

  return classNames(normalState[finalVariant], activeState[finalVariant], hoverState[finalVariant], {
    'cursor-not-allowed': disabled,
  })
}

const buttonClasses = {
  standard: 'btn-standard',
  xs: 'btn-xs',
  sm: 'btn-sm',
  md: 'btn-md',
  lg: 'btn-lg',
}
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'standard'

export function getButtonSizeClasses(size: ButtonSize) {
  return buttonClasses[size]
}
